import { InformationModal } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useGetLogActivityDetail } from "@/hooks/logActivityHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";

interface LogDetailModalProps {
  open: boolean;
  onClose: () => void;
  selectedId?: number;
}

const parseJsonSafely = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

const renderValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const LogDetailModal = ({ open, onClose, selectedId }: LogDetailModalProps) => {
  const {
    data: logActivityDetail,
    isFetching: isDetailLoading,
    isError: isDetailError,
  } = useGetLogActivityDetail(selectedId ?? 0);

  useQueryHandler({
    isFetching: isDetailLoading,
    isError: isDetailError,
    errorMessage: "ไม่สามารถโหลดรายละเอียด Log ได้",
  });

  const renderChangesTable = () => {
    if (!logActivityDetail) return null;

    const oldValues = parseJsonSafely(logActivityDetail.old_values);
    const newValues = parseJsonSafely(logActivityDetail.new_values);

    if (!oldValues && !newValues) {
      return (
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          ไม่มีข้อมูลการเปลี่ยนแปลง
        </Typography>
      );
    }

    const allKeys = new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]);

    return (
      <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>
                Field
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                ค่าเดิม
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                ค่าใหม่
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allKeys).map((key) => {
              const oldValue = oldValues?.[key] ?? null;
              const newValue = newValues?.[key] ?? null;
              const isChanged =
                JSON.stringify(oldValue) !== JSON.stringify(newValue);

              return (
                <TableRow
                  key={key}
                  sx={{
                    backgroundColor: isChanged ? "#fff3e0" : "transparent",
                    "&:hover": {
                      backgroundColor: isChanged ? "#ffe0b2" : "#f5f5f5",
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                  <TableCell
                    sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                  >
                    {renderValue(oldValue)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      color: isChanged ? "primary.main" : "inherit",
                      fontWeight: isChanged ? 600 : 400,
                    }}
                  >
                    {renderValue(newValue)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh", width: "70vw" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2" sx={{ mb: 3 }}>
          รายละเอียด Log ID: {selectedId}
        </Typography>
        {logActivityDetail ? (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  หมวดหมู่
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {logActivityDetail.resource}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  หมวดหมู่ย่อย
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {logActivityDetail.entity}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Entity ID (สำหรับค้นหาในฐานข้อมูล)
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {logActivityDetail.entity_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ดำเนินการโดย
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {logActivityDetail.performed_by}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Action
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {logActivityDetail.action}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Timestamp
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {new Date(logActivityDetail.time_stamp).toLocaleString(
                    "th-TH",
                  )}
                </Typography>
              </Box>
              {logActivityDetail.remark && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    หมายเหตุ
                  </Typography>
                  <Typography variant="body1" fontWeight={500} noWrap >
                    {logActivityDetail.remark}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              รายละเอียดการเปลี่ยนแปลง
            </Typography>
            {renderChangesTable()}
          </Box>
        ) : (
          <Typography variant="body1" mt={2} color="text.secondary">
            ไม่พบรายละเอียดสำหรับ Log นี้
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default LogDetailModal;
